<template>
  <form @submit.prevent class="mb-8">
    <div class="flex flex-col space-y-2 xs:flex-row xs:space-y-0">
      <TextInput
        id="fname"
        forInput="fname-input"
        v-model="contact.first_name"
        label="First Name"
        classes="w-full xs:w-1/2 xs:pr-2"
        placeholder="First Name"
      />
      <TextInput
        id="lname"
        forInput="lname-input"
        v-model="contact.last_name"
        label="Last Name"
        classes="w-full xs:w-1/2"
        placeholder="Last Name"
      />
    </div>
    <div class="flex flex-col space-y-2 mt-4 xs:flex-row xs:space-y-0 mb-8">
      <div class="w-full xs:w-3/5 xs:pr-2">
        <TextInput
          id="email"
          forInput="email-input"
          type="email"
          v-model="contact.email"
          label="Email"
          placeholder="Email"
          :inputClasses="`transition duration-200 ease-in-out ${
            emailError ? '!border-[#df1b41] !text-[#df1b41]' : 'border-transparent'
          }`"
          @blur="handleEmailBlur"
          @update:modelValue="handleEmailUpdate"
        />
        <p v-if="emailError" class="text-[#df1b41] text-sm mt-0.5" id="email-error-message">
          {{ emailError }}
        </p>
      </div>
      <div class="w-full xs:w-2/5">
        <TextInput
          id="tel"
          forInput="tel-input"
          type="tel"
          v-model="contact.phone"
          label="Phone"
          placeholder="Phone"
          classes="w-full"
          :inputClasses="`transition duration-200 ease-in-out ${
            phoneError ? '!border-[#df1b41] !text-[#df1b41]' : 'border-transparent'
          }`"
          @blur="handlePhoneBlur"
          @update:modelValue="handlePhoneUpdate"
        />
        <p v-if="phoneError" class="text-[#df1b41] text-sm mt-0.5" id="phone-error-message">
          {{ phoneError }}
        </p>
      </div>
    </div>
  </form>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { computed, onMounted, ref } from 'vue'
import { z } from 'zod'

import TextInput from '@/common/components/molecules/TextInput.vue'
import { useCheckoutStore } from '@/stores/checkout'

const checkoutStore = useCheckoutStore()
const { contact, hasSufficientGuestInfo } = storeToRefs(checkoutStore)

// Email validation
const emailSchema = z.email({ message: 'Please enter a valid email address.' })
const emailError = ref<string>('')
const isEmailValid = computed<boolean>(() => emailError.value === '')
function validateEmail(email: string): void {
  const result = emailSchema.safeParse(email)
  if (!result.success) emailError.value = result.error.issues[0].message
  else emailError.value = ''
}
onMounted(handleEmailBlur)
function handleEmailBlur(): void {
  if (contact.value.email.length > 0) validateEmail(contact.value.email)
  else emailError.value = ''
}
function handleEmailUpdate(email: string): void {
  if (email.length === 0) emailError.value = ''
}

// Phone validation
const phoneSchema = z
  .string()
  .max(20, { message: 'Please ensure this field has no more than 20 characters.' })
const phoneError = ref<string>('')
const isPhoneValid = computed<boolean>(() => phoneError.value === '')
function validatePhone(phone: string): void {
  const result = phoneSchema.safeParse(phone)
  if (!result.success) phoneError.value = result.error.issues[0].message
  else phoneError.value = ''
}
onMounted(handlePhoneBlur)
function handlePhoneBlur(): void {
  if (contact.value.phone.length > 0) validatePhone(contact.value.phone)
  else phoneError.value = ''
}
function handlePhoneUpdate(phone: string): void {
  if (phone.length === 0) phoneError.value = ''
}

// Form state
const isContactFormValid = computed<boolean>(
  () => hasSufficientGuestInfo.value && isEmailValid.value && isPhoneValid.value,
)
defineExpose({ isContactFormValid })
</script>
